import type {
  CollectionRef,
  ColorValue,
  ImageValue,
  LinkValue,
  ProductRef,
  RichtextValue,
  SettingField,
  SettingValue,
  VideoValue,
} from '@aquilixshop/ui-react';
import { TextControl } from './controls/TextControl';
import { TextareaControl } from './controls/TextareaControl';
import { RichtextControl } from './controls/RichtextControl';
import { ImageControl } from './controls/ImageControl';
import { VideoControl } from './controls/VideoControl';
import { LinkControl } from './controls/LinkControl';
import { NumberControl } from './controls/NumberControl';
import { DatetimeControl } from './controls/DatetimeControl';
import { RangeControl } from './controls/RangeControl';
import { CheckboxControl } from './controls/CheckboxControl';
import { SelectControl } from './controls/SelectControl';
import { ColorControl } from './controls/ColorControl';
import { GradientControl } from './controls/GradientControl';
import { IconControl } from './controls/IconControl';
import { IconNameControl } from './controls/IconNameControl';
import { MenuControl } from './controls/MenuControl';
import { ProductControl } from './controls/ProductControl';
import { CollectionControl } from './controls/CollectionControl';
import { CollectionBlocksControl } from './controls/CollectionBlocksControl';

/**
 * The closed control switch (build-plan §6.1) — ONE generator, never a hand-built form per section. TS
 * narrows `field` (and `field.default`) per case; the `never` default makes adding a `SettingFieldType`
 * member a COMPILE ERROR until a control exists here (the "closed v1 control set" enforced in code).
 *
 * Each control reads its value from the selected node's `settings[field.id]` (passed in) and reports
 * changes via `onChange`; the Inspector dispatches `updateSectionSettings`/`updateBlockSettings` with a
 * partial keyed patch (build-plan §6.2).
 */
export function FieldControl({
  field,
  value,
  onChange,
  disabled = false,
  inherited,
  sectionId,
}: {
  field: SettingField;
  value: SettingValue | undefined;
  onChange: (next: SettingValue | undefined) => void;
  /**
   * What this field paints while it holds no value of its own — the sibling named by `inheritFrom`,
   * already resolved (stored value, else that sibling's schema default) by the form. Only the range
   * control reads it; see `RangeField.inheritFrom` for why an absent value is a state and not a bug.
   */
  inherited?: SettingValue | undefined;
  /**
   * The field is LOCKED by the store's configuration — the schema offers the choice, this store has only
   * one valid answer for it (see `SectionForm`'s cart lock). Honoured by the controls that can show a
   * greyed value; the others ignore it, so a caller must only pass it for a field it has checked.
   */
  disabled?: boolean;
  /** The section this field belongs to — only the block-editing controls need it. */
  sectionId?: string | null;
}) {
  switch (field.type) {
    case 'text':
      return <TextControl field={field} value={value as string | undefined} onChange={onChange} />;
    case 'textarea':
      return (
        <TextareaControl field={field} value={value as string | undefined} onChange={onChange} />
      );
    case 'richtext':
      return (
        <RichtextControl
          field={field}
          value={value as RichtextValue | undefined}
          onChange={onChange}
        />
      );
    case 'image':
      return (
        <ImageControl field={field} value={value as ImageValue | undefined} onChange={onChange} />
      );
    case 'video':
      return (
        <VideoControl field={field} value={value as VideoValue | undefined} onChange={onChange} />
      );
    case 'link':
      return (
        <LinkControl field={field} value={value as LinkValue | undefined} onChange={onChange} />
      );
    case 'number':
      return (
        <NumberControl field={field} value={value as number | undefined} onChange={onChange} />
      );
    case 'range':
      return (
        <RangeControl
          field={field}
          value={value as number | undefined}
          onChange={onChange}
          inherited={typeof inherited === 'number' ? inherited : undefined}
        />
      );
    case 'datetime':
      return (
        <DatetimeControl field={field} value={value as string | undefined} onChange={onChange} />
      );
    case 'checkbox':
      return (
        <CheckboxControl
          field={field}
          value={value as boolean | undefined}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'select':
      return (
        <SelectControl field={field} value={value as string | undefined} onChange={onChange} />
      );
    case 'color':
      return (
        <ColorControl field={field} value={value as ColorValue | undefined} onChange={onChange} />
      );
    case 'gradient':
      return (
        <GradientControl field={field} value={value as string | undefined} onChange={onChange} />
      );
    case 'icon':
      return <IconControl field={field} value={value as string | undefined} onChange={onChange} />;
    case 'icon-name':
      return (
        <IconNameControl field={field} value={value as string | undefined} onChange={onChange} />
      );
    case 'menu':
      return <MenuControl field={field} value={value as string | undefined} onChange={onChange} />;
    case 'product-picker':
      return (
        <ProductControl field={field} value={value as ProductRef | undefined} onChange={onChange} />
      );
    case 'collection-picker':
      return (
        <CollectionControl
          field={field}
          value={value as CollectionRef | undefined}
          onChange={onChange}
        />
      );
    case 'collection-blocks':
      // Edits the OWNING section's blocks, so it needs to know which section it sits in; a block form
      // never carries this field (it is a section-level door onto the blocks).
      return <CollectionBlocksControl field={field} sectionId={sectionId ?? null} />;
    default: {
      // Adding a SettingFieldType member without a case above is a compile error here.
      const _exhaustive: never = field;
      return _exhaustive;
    }
  }
}
